import { Navigate } from 'react-router-dom'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { useOnboardingStatus } from '@/features/auth/hooks/useOnboardingStatus'
import { LoadingState } from './LoadingState'
import { Box, Heading, Text, VStack, Button } from '@chakra-ui/react'
import { AlertCircle } from 'lucide-react'
import { Link as RouterLink } from 'react-router-dom'

interface ProtectedIntranetRouteProps {
  children: React.ReactNode
  requiresResidence?: boolean
}

/**
 * Protected route for Intranet (USER, MANAGER, ADMIN only)
 * If requiresResidence is true, will redirect to onboarding if user has no residences
 */
export function ProtectedIntranetRoute({
  children,
  requiresResidence = true,
}: ProtectedIntranetRouteProps) {
  const { data: auth, isLoading } = useAuth()
  const { needsOnboarding, isLoading: isOnboardingLoading } = useOnboardingStatus()

  if (isLoading || isOnboardingLoading) {
    return <LoadingState />
  }

  // Check authentication
  if (!auth?.session) {
    return <Navigate to="/app/login" replace />
  }

  // CRITICAL: If user has pending invitation status, force them to complete password setup
  // This prevents users authenticated via magic link from accessing the app without setting a password
  if (auth.profile?.invitation_status === 'pending') {
    return <Navigate to="/auth/welcome" replace />
  }

  // Check if user has intranet role (USER, MANAGER, ADMIN, or ACCOUNTANT)
  const hasIntranetRole = auth.residences.some((r) =>
    ['USER', 'MANAGER', 'ADMIN', 'ACCOUNTANT'].includes(r.role)
  )

  // If user has no residences but has an agency, redirect to onboarding
  // (unless we're already on a route that doesn't require residences, like /onboarding itself)
  if (requiresResidence && needsOnboarding) {
    return <Navigate to="/app/onboarding" replace />
  }

  // If user has no intranet role and no agency, deny access
  if (!hasIntranetRole && !needsOnboarding) {
    return (
      <Box minH="100vh" display="flex" alignItems="center" justifyContent="center" bg="gray.50" p={8}>
        <VStack gap={6} maxW="md" textAlign="center">
          <Box
            p={4}
            bg="red.100"
            borderRadius="full"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <AlertCircle size={40} color="#e53e3e" />
          </Box>
          <Heading size="xl" color="gray.900">
            Accès refusé
          </Heading>
          <Text color="gray.600" fontSize="lg">
            Vous n'avez pas les permissions nécessaires pour accéder à l'outil de gestion.
          </Text>
          <Text color="gray.600" fontSize="sm">
            L'accès à cette zone est réservé aux gestionnaires et administrateurs.
          </Text>
          <VStack gap={3} w="full" pt={4}>
            <RouterLink to="/app/extranet/login" style={{ width: '100%' }}>
              <Button
                colorPalette="brand"
                size="lg"
                w="full"
              >
                Accéder à l'extranet copropriétaire
              </Button>
            </RouterLink>
            <RouterLink to="/app/login" style={{ width: '100%' }}>
              <Button
                variant="outline"
                size="lg"
                w="full"
              >
                Retour à la connexion
              </Button>
            </RouterLink>
          </VStack>
        </VStack>
      </Box>
    )
  }

  return <>{children}</>
}
